class PbhTeamCarousel extends HTMLElement {
  constructor() {
    super();
    this.intervalMs = 12000;
    this.timer = null;
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onDot = this.onDot.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
  }

  connectedCallback() {
    this.isHomepageCapture = this.hasAttribute('data-homepage-capture');
    this.track = this.querySelector('[data-carousel-track]');
    this.controls = this.querySelector('.team-carousel__controls');
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.members = this.#loadMembers();

    if (this.members.length === 0) return;

    this.index = Number(this.dataset.startIndex || 0);
    if (this.index >= this.members.length) this.index = 0;

    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.dotHandlers = [];
    this.dots.forEach((dot, dotIndex) => {
      const handler = () => this.onDot(dotIndex);
      this.dotHandlers.push({ dot, handler });
      dot.addEventListener('click', handler);
    });

    document.addEventListener('visibilitychange', this.onVisibility);
    this.render(false);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.dotHandlers?.forEach(({ dot, handler }) => {
      dot.removeEventListener('click', handler);
    });
    this.dotHandlers = [];
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  #loadMembers() {
    const script = this.previousElementSibling;
    if (!script || script.tagName !== 'SCRIPT' || !script.hasAttribute('data-team-carousel-members')) {
      return [];
    }
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  #escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  #buildSlide(member) {
    const linkedinLabel = this.dataset.linkedinLabel || 'LinkedIn';
    const isHomepageCapture = this.hasAttribute('data-homepage-capture');
    const linkedin = member.linkedin
      ? `<a class="team-grid__linkedin" href="${this.#escapeHtml(member.linkedin)}" rel="noopener noreferrer" target="_blank" aria-label="${this.#escapeHtml(linkedinLabel)}"><svg class="icon icon--linkedin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 114.127 0 2.062 2.062 0 01-2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg></a>`
      : '';

    const role = member.role
      ? `<p class="team-grid__role">${this.#escapeHtml(member.role)}</p>`
      : '';
    const bio = member.bio || member.readMore || '';

    const bioHtml = bio
      ? isHomepageCapture
        ? bio
        : `<p class="team-grid__bio-text">${bio}</p>`
      : '';

    const imageWidth = isHomepageCapture ? 600 : 384;
    const imageHeight = isHomepageCapture ? '' : 512;
    const imageDimensions = isHomepageCapture
      ? `width="${imageWidth}"`
      : `width="${imageWidth}" height="${imageHeight}"`;

    return `
      <div class="team-grid__card team-carousel__slide">
        <div class="team-grid__media">
          <img class="team-grid__photo" src="${this.#escapeHtml(member.image)}" alt="${this.#escapeHtml(member.name)}" loading="lazy" ${imageDimensions}>
        </div>
        <div class="team-grid__content">
          <div class="team-grid__name-row">
            <h3 class="team-grid__name">${this.#escapeHtml(member.name)}</h3>
            ${linkedin}
          </div>
          ${role}
          ${bioHtml}
        </div>
      </div>
    `;
  }

  onPrev() {
    this.goTo(this.index - 1);
    this.start();
  }

  onNext() {
    this.goTo(this.index + 1);
    this.start();
  }

  onDot(dotIndex) {
    this.goTo(dotIndex);
    this.start();
  }

  onVisibility() {
    if (document.hidden) {
      this.stop();
      return;
    }
    this.start();
  }

  goTo(nextIndex) {
    if (this.members.length === 0) return;
    this.stop();
    this.index = (nextIndex + this.members.length) % this.members.length;
    this.render(true);
  }

  render(animate) {
    const member = this.members[this.index];
    if (!member) return;

    const slideHtml = this.#buildSlide(member);

    if (this.isHomepageCapture) {
      const existingSlide = this.querySelector('.team-carousel__slide');
      existingSlide?.remove();
      this.controls?.insertAdjacentHTML('beforebegin', slideHtml);
    } else {
      this.track.style.setProperty('--pbh-carousel-index', this.index);
      this.track.classList.toggle('team-carousel__track--animate', animate);
      this.track.innerHTML = slideHtml;
    }

    this.dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('team-carousel__dot--active', dotIndex === this.index);
      dot.setAttribute('aria-current', dotIndex === this.index ? 'true' : 'false');
    });
  }

  start() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stop();
    this.timer = window.setInterval(() => this.onNext(), this.intervalMs);
  }

  restart() {
    this.start();
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('pbh-team-carousel')) {
  customElements.define('pbh-team-carousel', PbhTeamCarousel);
}
